/**
 * The Wheel hub.
 *
 * Compact first-screen launcher for the daily puzzle and practice mode.
 */
import { motion } from 'motion/react';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { usePreferences } from '@/preferences';
import { WheelSounds } from '@/games/the-wheel/sound';
import { CATEGORIES, todayPuzzle } from '@/games/the-wheel/puzzles';
import { paintWheelPreview } from '@/games/the-wheel/rendering/createWheelTexture';

import './WheelHub.css';

interface ModeCard {
  slug: 'daily' | 'practice';
  title: string;
  eyebrow: string;
  blurb: string;
  cta: string;
}

const MODES: ReadonlyArray<ModeCard> = [
  {
    slug: 'daily',
    title: "Today's Puzzle",
    eyebrow: 'Today, together',
    blurb: 'Same puzzle for everyone. Start with the daily category and play it through.',
    cta: 'Play daily',
  },
  {
    slug: 'practice',
    title: 'Practice',
    eyebrow: 'Any time',
    blurb: 'Fresh puzzle, no streak pressure. Spin, solve, and keep going.',
    cta: 'Practice',
  },
];

export function WheelHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = usePreferences();
  const today = useMemo(() => todayPuzzle(), []);
  const todayCategory = CATEGORIES.find((c) => c.id === today.category)!;

  const pick = (slug: 'daily' | 'practice'): void => {
    WheelSounds.keyTap();
    const current = new URLSearchParams(location.search);
    const next = new URLSearchParams({ mode: slug });
    for (const key of ['wheelStudy', 'sectors']) {
      const value = current.get(key);
      if (value !== null) next.set(key, value);
    }
    navigate(`/play/wheel?${next.toString()}`);
  };

  return (
    <div className="wheel-hub" data-theme={theme}>
      <div className="wheel-hub__page">
        <PaperBackdrop />

        <motion.main
          className="wheel-hub__hero"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut', delay: 0.04 }}
        >
          <section className="wheel-hub__copy" aria-labelledby="wheel-hub-title">
            <p className="wheel-hub__eyebrow">Rendever Games</p>
            <h1 className="wheel-hub__title" id="wheel-hub-title">
              The Wheel
            </h1>
            <p className="wheel-hub__lede">
              Spin, call a letter, reveal the phrase, and solve when you know it.
            </p>

            <div
              className="wheel-hub__daily-note"
              aria-label={`Today's category is ${todayCategory.label}`}
            >
              <span>Today&apos;s category</span>
              <strong>{todayCategory.label}</strong>
              <em>{todayCategory.hint}</em>
            </div>
          </section>

          <WheelPreview />

          <section className="wheel-hub__choices" aria-label="Choose how to play">
            <ul className="wheel-hub__cards" role="list">
              {MODES.map((mode, index) => (
                <motion.li
                  key={mode.slug}
                  className="wheel-hub__card-item"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 240,
                    damping: 24,
                    delay: 0.12 + index * 0.06,
                  }}
                >
                  <button
                    type="button"
                    className="wheel-hub__card"
                    data-slug={mode.slug}
                    onClick={() => pick(mode.slug)}
                  >
                    <span className="wheel-hub__card-main">
                      <span className="wheel-hub__card-eyebrow">{mode.eyebrow}</span>
                      <span className="wheel-hub__card-title">{mode.title}</span>
                      <span className="wheel-hub__card-blurb">{mode.blurb}</span>
                    </span>
                    <span className="wheel-hub__card-cta" aria-hidden="true">
                      {mode.cta} →
                    </span>
                  </button>
                </motion.li>
              ))}
            </ul>
          </section>
        </motion.main>
      </div>
    </div>
  );
}

function WheelPreview() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current) paintWheelPreview(canvasRef.current);
  }, []);

  return (
    <section className="wheel-hub__preview" aria-hidden="true">
      <div className="wheel-hub__wheel-stage">
        <div className="wheel-hub__pointer">
          <span />
        </div>
        <canvas ref={canvasRef} className="wheel-hub__wheel-canvas" />
      </div>
      <div className="wheel-hub__preview-caption">
        <strong>Spin. Reveal. Solve.</strong>
        <span>A fresh phrase every day.</span>
      </div>
    </section>
  );
}

function PaperBackdrop() {
  return (
    <>
      <div className="wheel-hub__paper" aria-hidden="true">
        <svg width="100%" height="100%" aria-hidden="true">
          <defs>
            <filter id="wheel-hub-grain" x="0" y="0" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.82" numOctaves="2" seed="14" />
              <feColorMatrix values="0 0 0 0 0.14  0 0 0 0 0.11  0 0 0 0 0.07  0 0 0 0.07 0" />
            </filter>
          </defs>
          <rect width="100%" height="100%" filter="url(#wheel-hub-grain)" />
        </svg>
      </div>
      <div className="wheel-hub__paper-edge" aria-hidden="true" />
    </>
  );
}
